import React from "react";
import { ArrowRight, TrendingUp, Activity, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  value: 100
}, {
  month: "Feb",
  value: 120
}, {
  month: "Mar",
  value: 115
}, {
  month: "Apr",
  value: 160
}, {
  month: "May",
  value: 150
}, {
  month: "Jun",
  value: 210
}];
const chartConfig = {
  value: {
    label: "Protection Level",
    color: "var(--chart-1)"
  }
};
export default function RecoverValueFromSpentCoCatalyst() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="20052" id="the-island-shield" className="dark bg-background text-foreground relative overflow-hidden py-16 lg:py-24">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-sky-500/10 via-background to-background opacity-50" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          {}
          <div ref={ref} className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.1]">
                Invisible Armor for Your <br />
                <span className="text-primary">Island Property</span>
              </h1>
              
              <div className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-medium bg-gradient-to-r from-white via-slate-300 to-slate-500 bg-clip-text text-transparent uppercase tracking-wider">
                  PREMIUM PROTECTION: WATERPROOFING & SEALING
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
                  Island Seals provides professional protection for concrete and 
                  stone surfaces, resisting moisture, chemicals, and wear for 
                  lasting beauty and structural integrity.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-md">
                <Link to="/RequestQuotePage">
                  Get Free Assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary/50 text-primary hover:bg-primary/10 hover:text-primary hover:border-primary rounded-md">
                <Link to="/ProjectGalleryPage">
                  View Our Work
                </Link>
              </Button>
            </div>

            {}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-8 border-t border-border/30">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium uppercase tracking-tight">Waterproof Shield</span>
              </div>
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium uppercase tracking-tight">Enhanced Durability</span>
              </div>
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium uppercase tracking-tight">Expert Assessment</span>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 relative h-[500px] lg:h-[600px] w-full hidden lg:block">
            {}
            <div className="absolute top-0 right-0 w-[90%] h-[85%] rounded-md overflow-hidden shadow-2xl border border-border/20">
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent z-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealed_foundation_protection.png" alt="Sealed concrete foundation with water protection" className="w-full h-full object-cover" />
            </div>

            {}
            <div className="absolute bottom-8 left-0 w-[65%] z-20 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 fill-mode-forwards">
              <Card className="bg-card/95 backdrop-blur-md border-border/50 shadow-2xl overflow-hidden rounded-md">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Surface Integrity
                  </CardTitle>
                  <CardDescription>
                    Protection levels over time
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-32 w-full">
                    <ChartContainer config={chartConfig} className="h-full w-full">
                      <AreaChart data={chartData} margin={{
                      top: 5,
                      right: 0,
                      left: 0,
                      bottom: 0
                    }}>
                        <defs>
                          <linearGradient id="fillValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--color-chart-1)" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="var(--color-chart-1)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} hide />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" hideLabel />} />
                        <Area dataKey="value" type="natural" fill="url(#fillValue)" stroke="var(--color-chart-1)" strokeWidth={2} />
                      </AreaChart>
                    </ChartContainer>
                  </div>
                  <div className="p-4 pt-0 flex items-center gap-4">
                     <div className="h-12 w-12 rounded-sm overflow-hidden shrink-0 border border-border/50">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/precious_metal_grains.png" alt="Sealed stone texture" className="h-full w-full object-cover" />
                     </div>
                     <div>
                        <p className="text-sm font-medium text-foreground">Precision Application</p>
                        <p className="text-xs text-muted-foreground">High performance finish</p>
                     </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {}
          <div className="lg:hidden relative h-[300px] w-full rounded-md overflow-hidden mt-8">
             <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealed_foundation_protection.png" alt="Sealed concrete foundation with water protection" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </section>;
}